import { API_BASE_URL } from './api'

export interface PortalCustomerInfo {
  name: string
  email: string
  phone?: string
}

export interface PortalQueue {
  queue_id: string
  queue_name: string
  description?: string
}

export interface PortalQueuesResponse {
  company_name: string
  app_name: string
  queues: PortalQueue[]
}

export interface PortalStartResponse {
  portal_token: string
  conversation_id: string
  customer_id: string
  expires_ts: string
}

export interface PortalMessage {
  message_id: string
  content: string
  content_type: string
  sender_type: 'customer' | 'agent' | 'system' | 'auto'
  sender_name: string
  sender_initials?: string
  is_secure?: boolean
  created_ts: string
  attachments: Array<{
    attachment_id: string
    original_filename: string
    file_size: number
    mime_type: string
  }>
}

export interface PortalConversationResponse {
  conversation: {
    conversation_id: string
    subject: string
    status: string
    priority: string
    channel: string
    is_flagged: boolean
    is_resolved: boolean
    is_secure: boolean
    unread_count: number
    last_message_ts: string | null
    created_ts: string
  }
  customer: {
    customer_id: string
    customer_name: string
    email: string
    phone: string | null
    avatar: string | null
  }
  app: {
    app_id: string
    app_name: string
  }
  company: {
    company_id: string
    company_name: string
  }
  queue: {
    queue_id: string
    queue_name: string
  } | null
  assigned_user: {
    user_id: string
    name: string
    initials: string
  } | null
  statistics: {
    total_messages: number
    customer_messages: number
    agent_messages: number
    system_messages: number
    unread_messages: number
    total_attachments: number
    first_message_ts: string | null
    latest_message_ts: string | null
  }
  portal_session: {
    expires_ts: string
    last_activity_ts: string | null
    created_ts: string
  }
  endpoints: {
    poll: string
    send_message: string
    attachments: string
  }
}

export interface PortalPollResponse {
  messages: PortalMessage[]
  conversation_status: string
  has_more: boolean
  session_expires_ts: string
  is_agent_typing?: boolean
}

// Start a portal session
export async function startPortalSession(
  appId: string,
  customerInfo: PortalCustomerInfo,
  initialMessage: string,
  queueId?: string
): Promise<PortalStartResponse> {
  const response = await fetch(`${API_BASE_URL}/portal/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      app_id: appId,
      customer_info: customerInfo,
      initial_message: initialMessage,
      ...(queueId && { queue_id: queueId }),
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to start portal session')
  }

  const result = await response.json()
  return result.data
}

// Get public queues for an app
export async function getPortalQueues(appId: string): Promise<PortalQueuesResponse> {
  const response = await fetch(`${API_BASE_URL}/portal/queues?app_id=${appId}`)

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to fetch queues')
  }

  const result = await response.json()
  return result.data
}

// Get conversation details
export async function getPortalConversation(portalToken: string): Promise<PortalConversationResponse> {
  const response = await fetch(`${API_BASE_URL}/portal/conversation?token=${portalToken}`)

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.error?.message || errorData.message || 'Failed to fetch conversation'
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    ;(error as any).originalResponse = errorData
    throw error
  }

  const result = await response.json()
  return result.data
}

// Poll for new messages
export async function pollPortalMessages(
  portalToken: string,
  lastMessageId?: string
): Promise<PortalPollResponse> {
  const params = new URLSearchParams({ token: portalToken })
  if (lastMessageId) {
    params.append('last_message_id', lastMessageId)
  }

  const response = await fetch(`${API_BASE_URL}/portal/poll?${params}`)

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.error?.message || errorData.message || 'Failed to poll messages'
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    ;(error as any).originalResponse = errorData
    throw error
  }

  const result = await response.json()
  return result.data
}

// Set typing indicator
export async function setPortalTypingIndicator(
  portalToken: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/portal/typing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${portalToken}`,
    },
    body: JSON.stringify({}),
  })

  if (!response.ok) {
    const errorData = await response.json()
    const errorMessage = errorData.error?.message || errorData.message || 'Failed to set typing indicator'
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    ;(error as any).originalResponse = errorData
    throw error
  }
}

// Send a message
export async function sendPortalMessage(
  portalToken: string,
  content: string,
  attachmentIds?: string[]
): Promise<{ message_id: string; created_ts: string }> {
  const response = await fetch(`${API_BASE_URL}/portal/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${portalToken}`,
    },
    body: JSON.stringify({
      content,
      ...(attachmentIds && attachmentIds.length > 0 && { attachments: attachmentIds }),
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to send message')
  }

  const result = await response.json()
  return result.data
}

// Upload an attachment
export async function uploadPortalAttachment(
  portalToken: string,
  file: File
): Promise<{
  attachment_id: string
  original_filename: string
  file_size: number
  mime_type: string
  created_ts: string
}> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`${API_BASE_URL}/portal/attachment/upload?token=${portalToken}`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to upload attachment')
  }

  const result = await response.json()
  return result.data
}

// Get attachment download URL
export function getPortalAttachmentUrl(attachmentId: string, portalToken: string): string {
  return `${API_BASE_URL}/portal/attachment/download/${attachmentId}?token=${portalToken}`
}